import commonMixin from './../mixins/_common';
import modalMixin from './../mixins/_modal';
import flashMessageMixin from './../mixins/_flash_messages';
import bannerTemplateSelecter from './_banner_template_selecter';
import imagesConfigMixin from './../mixins/_images_config';
import storecreatorImageSelect from './_storecreator_image_select';

// 最大画像容量 100MB(413エラーが解消されない為、フロントでは30M制限)
const MAX_IMAGE_SIZE = 30000000;

/**
 * 画像の選択、UPロードを操作するコンポーネント
 */
export default {
    name: 'ImageSelecter',
    template: '#image_selecter_template',
    data() {
        return {
            // 表示中画面
            display: null,
            // URLから登録する場合のフォーム
            url: null,
            // PCから取り込むinputタグの再描画用フラグ
            toggle_file_input: true,
            // image_selecterモーダルの描画フラグ
            open_image_selecter: this.open_image_selecter_flag,
            // バナーテンプレートモーダルが開かれているか否か
            open_banner_template_selecter: false,
        };
    },
    props: {
        // 指定の画像ファイル名
        image: {
            type: String,
            required: false,
            default: ''
        },
        // 詳細画像一覧
        lib_images_config: {
            type: Array,
            require: false,
            default: null,
        },
        // 画像の保存先
        page_dir: {
            type: String,
            required: false,
            default: 'common',
            validator: function (value) {
                // プロパティの値は、必ずいずれかの文字列でなければならない
                return ['common', 'build_store', 'item', 'custom_page'].indexOf(value) !== -1;
            }
        },
        // 保存する画像の接頭語
        edit_id_dir: {
            type: [String, Number],
            required: false,
            default: null
        },
        // 画像編集しないで画像表示のみ
        only_preview: {
            type: Boolean,
            required: false,
            default: false
        },
        // 初期の検索モード item|lib
        image_mode: {
            type: String,
            required: false,
            default: 'lib'
        },
        // ストクリへ即時UPロード item|lib|null
        realtime_upload: {
            type: String,
            required: false,
            default: 'lib'
        },
        // 初期表示画面
        prop_display: {
            type: String,
            required: false,
            default: 'select_method',
            validator: function (value) {
                // 選択可能画面
                return ['select_method', 'select_image_list', 'input_url'].indexOf(value) !== -1;
            }
        },
        // image_selecterモーダルの描画フラグ
        // (build_storeの初期看板生成時にだけtrueが渡ってくる)
        open_image_selecter_flag: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    mixins: [
        commonMixin,
        modalMixin,
        flashMessageMixin,
        imagesConfigMixin,
    ],
    components: {
        // バナーテンプレート画像選択コンポーネント
        'banner-template-selecter': bannerTemplateSelecter,
        // Yahoo!ストアクリエイターの「画像管理」から画像を選択
        'storecreator-image-select': storecreatorImageSelect
    },
    computed: {
        // 最大画像容量を超えた時に表示するメッセージ
        overMaxImageSizeMessage: function () {
            return '画像のデータサイズが' + this.$_formatBytes(MAX_IMAGE_SIZE, 1000) + 'を超えています。サイズを小さくしてアップロードをお願いします。';
        },
    },
    emits: [
        'update:image',
    ],
    methods: {
        // image_selecterモーダルを開く
        openImageSelecter: async function () {
            if (!this.open_image_selecter) {
                this.open_image_selecter = true;
                // DOM側の描画を待つ
                await this.$nextTick();
                // モーダルを.main直下に移動させる
                this.iniModal('#modal_image_selecter');
                // 多重モーダルの表示順序を取り直す
                this.multipleModal();
                // モーダルの初期化
                this.opening();
                // モーダルを開く
                this.open();
            } else {
                this.open();
            }
        },
        // モーダルを開く時に初期化
        opening: function () {
            this.clearFlashMessage();
            this.url = null;

            switch (this.prop_display) {
                case 'select_image_list':
                    this.storecreatorImageSelect();
                    break;
                case 'input_url':
                    this.display = this.prop_display;
                    break;
                default:
                    this.display = 'select_method';
                    break;
            }
        },
        // 戻るボタンをクリック
        back: function () {
            if (this.display === 'select_method' || this.prop_display !== 'select_method') {
                this.close();
            } else {
                this.display = 'select_method';
            }
        },
        // file inputタグを再描画
        resetFileInput: async function () {
            this.toggle_file_input = false;
            await this.$nextTick();
            this.toggle_file_input = true;
        },
        /**
         * PCから画像を選択
         * 
         * @param event
         */
        upload: async function (event) {
            // file inputタグを再描画
            await this.resetFileInput();
            // フラッシュメッセージを初期化
            this.clearFlashMessage();

            if (event.target.files.length === 0) {
                return;
            }
            const file = event.target.files[0];

            this.$_loading();
            // 連続で容量オーバー画像をUPされた時のエラーメッセージを認識しやすくする為一瞬待つ
            await this.$_sleep(500);

            // 容量制限
            if (file.size > MAX_IMAGE_SIZE) {
                this.appendFlashMessage({
                    level: 'alert-danger',
                    message: this.overMaxImageSizeMessage,
                });
                this.$_unloading();
                return;
            }
            // ファイルUPロード
            await this.uploadFormData(file);
            this.$_unloading();
        },
        /**
         * サーバへ画像ファイルをアップロード
         * 
         * @param {Object} file
         */
        uploadFormData: async function (file) {
            // ファイルUPロード
            const form = new FormData();
            form.append('files[]', file);
            form.append('edit_id_dir', this.edit_id_dir);
            form.append('page_dir', this.page_dir);

            if (this.realtime_upload) {
                form.append('realtime_upload', this.realtime_upload);
            }

            try {
                const response = await axios.post('/upload_image', form);
                if (response.data.status === 'OK') {
                    // サーバーから返ってきたファイル名で更新
                    this.emitUpdateImage(response.data.data.file_names[0]);
                    this.close();
                }

            } catch (e) {
                const message = (e.response.status === 413) ?
                    this.overMaxImageSizeMessage :
                    this.getCatchMessage(e);

                this.appendFlashMessage({
                    level: 'alert-danger',
                    message: message,
                });
            };
        },
        // ストアクリエイター画像選択画面へ切り替える
        storecreatorImageSelect: async function () {
            // 追加画像をデフォルトで表示
            if(await this.$refs.storecreator_image_select.getImageList(1, 'lib') &&
                await this.$refs.storecreator_image_select.getLibDirectoryList()) {
                this.display = 'select_image_list';
            }
        },
        // URLから画像選択
        urlSelect: function (event) {
            // バリデーションエラーの場合はそのままフォーム送信する
            if (!event?.target?.form?.checkValidity()) {
                return;
            }
            event.preventDefault();

            this.emitUpdateImage(this.url);
            this.close();
        },
        // 画像管理からの選択
        imageListSelect: function (url) {
            this.emitUpdateImage(url);
            this.close();
        },
        // バナーテンプレートモーダルを開く
        openModalBannerTemplate: async function() {
            this.open_banner_template_selecter = true;
            await this.$nextTick(function() {
                this.$refs.bannerTemplateSelecter.openModal();
            }, this);
        },

        // -------------------------
        // emit
        // -------------------------

        // ファイル名を更新する
        emitUpdateImage: function (fileName) {
            this.$emit('update:image', fileName);
        }
    }
};
